/*
 * Keep Spreadconnect collection-card images aligned with sellable colors.
 * If a featured image belongs to a fully unavailable color, Wonder selects
 * the first available color swatch. Fully sold-out products are unchanged.
 */
(() => {
  /*
   * Spreadconnect does not expose whether a secondary mockup is framed larger
   * than its primary image. Keep this presentation-only inset conservative:
   * only verified oversized hover assets are listed here.
   */
  const insetHoverColors = new Map([
    [
      "joshs-pickles-unisex",
      new Set([
        "black",
        "dark red",
        "gravel",
        "military green",
        "powder blue",
        "white",
        "natural",
      ]),
    ],
    ["i-need-a-hug", new Set(["white", "gravel", "powder blue"])],
    [
      "joshs-pickles-hoodie",
      new Set(["sand", "asphalt gray", "black", "forest green", "navy"]),
    ],
    [
      "womens-i-need-a-huge-jar-of-pickles",
      new Set(["white", "light blue", "off white"]),
    ],
  ]);

  /*
   * Follow Wonder's rendered swatch-to-hover relationships instead of color
   * names or CDN filenames so image replacement and option reordering stay
   * safe. The Onesie has one first-option lifestyle image; the sticker has a
   * lifestyle image for every finish.
   */
  const insetLinkedHoverModes = new Map([
    ["powered-by-pickles-baby-onesie", "first"],
    ["joshs-pickles-logo-sticker", "all"],
    ["joshs-pickles-logo-snapback-baseball-cap", "all"],
    ["joshs-pickles-logo-kids-hoodie", "all"],
  ]);

  const imageKey = (value) => {
    if (!value) return "";

    try {
      const url = new URL(value, window.location.href);
      const version = url.searchParams.get("v");
      return `${url.pathname}${version ? `?v=${version}` : ""}`;
    } catch (_error) {
      return value;
    }
  };

  const syncCardHoverInset = (card) => {
    if (!card.querySelector("[data-jp-spreadconnect]")) return;

    const handle = card.querySelector(".card__picture")?.dataset.uri;
    const insetColors = insetHoverColors.get(handle);
    const linkedHoverMode = insetLinkedHoverModes.get(handle);
    const hoverImage = card.querySelector(".card__img--hover");
    if ((!insetColors && !linkedHoverMode) || !hoverImage) {
      card.removeAttribute("data-jp-hover-inset");
      return;
    }

    const currentHover = imageKey(hoverImage.getAttribute("src"));
    const firstColorHover = imageKey(
      card.querySelector(".color-swatcher[data-hover]:not([data-hover=''])")?.dataset.hover,
    );
    const matchesLinkedHover = [...card.querySelectorAll(".color-swatcher[data-hover]")].some(
      (swatch) => currentHover === imageKey(swatch.dataset.hover),
    );
    const isVerifiedInset =
      (linkedHoverMode === "first" &&
        Boolean(firstColorHover) &&
        currentHover === firstColorHover) ||
      (linkedHoverMode === "all" && matchesLinkedHover) ||
      [...card.querySelectorAll(".color-swatcher[data-hover]")].some(
        (swatch) =>
          insetColors?.has(normalizeLabel(swatch.dataset.color)) &&
          currentHover === imageKey(swatch.dataset.hover),
      );
    card.toggleAttribute("data-jp-hover-inset", isVerifiedInset);
  };

  const syncCardImage = (card) => {
    if (!card.querySelector("[data-jp-spreadconnect]")) return;

    const image = card.querySelector(".card__img");
    if (!image) return;

    const currentImages = new Set(
      [image.getAttribute("src"), image.currentSrc].map(imageKey).filter(Boolean),
    );
    const unavailableSwatches = card.querySelectorAll(
      ".color-swatcher--wrapper.unavailable .color-swatcher[data-img]",
    );
    const featuredColorIsUnavailable = [...unavailableSwatches].some((swatch) =>
      currentImages.has(imageKey(swatch.dataset.img)),
    );

    if (!featuredColorIsUnavailable) return;

    const firstAvailableSwatch = card.querySelector(
      ".color-swatcher--wrapper:not(.unavailable) .color-swatcher[data-img]:not([data-img=''])",
    );
    firstAvailableSwatch?.closest(".color-swatcher--wrapper")?.click();
  };

  const syncCards = (root) => {
    const cards = root.matches?.("color-swatch")
      ? [root]
      : root.querySelectorAll?.("color-swatch") || [];
    [...cards].forEach((card) => {
      syncCardImage(card);
      syncCardHoverInset(card);
    });
  };

  const syncAfterWonder = (root) => {
    customElements.whenDefined("color-swatch").then(() => {
      requestAnimationFrame(() => syncCards(root));
    });
  };

  const normalizeLabel = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  /*
   * Wonder calculates option availability from left to right, which means a
   * selected color updates sizes but a selected size does not update colors.
   * For standard-size Spreadconnect catalogs, compare each option against all
   * other current selections so availability works in both directions.
   * High-variant products stay on Wonder's server-rendered path because their
   * complete variant matrix is intentionally not present in the page HTML.
   */
  const getSpreadconnectVariantData = (variantOptions) => {
    if (
      !variantOptions?.matches?.("variant-options[data-jp-spreadconnect]") ||
      variantOptions.hasAttribute("data-high-variants-product")
    ) {
      return null;
    }

    try {
      const variants = variantOptions.getVariantData?.();
      return Array.isArray(variants) ? variants : null;
    } catch (_error) {
      return null;
    }
  };

  const syncSpreadconnectOptionAvailability = (variantOptions) => {
    const variants = getSpreadconnectVariantData(variantOptions);
    if (!variants) return;

    const optionGroups = [...variantOptions.querySelectorAll(".product-form__input")];
    const selectedValues = optionGroups.map((group) =>
      normalizeLabel(group.querySelector(":checked")?.value),
    );

    optionGroups.forEach((group, optionIndex) => {
      const candidates = group.querySelectorAll('input[type="radio"], option');

      candidates.forEach((candidate) => {
        const candidateValue = normalizeLabel(candidate.value);
        const isAvailable = variants.some((variant) => {
          if (!variant.available) return false;
          if (normalizeLabel(variant.options?.[optionIndex]) !== candidateValue) return false;

          return selectedValues.every((selectedValue, selectedIndex) => {
            if (selectedIndex === optionIndex || !selectedValue) return true;
            return normalizeLabel(variant.options?.[selectedIndex]) === selectedValue;
          });
        });

        candidate.classList.toggle("disabled", !isAvailable);
      });
    });
  };

  const selectOptionValue = (group, value) => {
    const normalizedValue = normalizeLabel(value);
    const radio = [...group.querySelectorAll('input[type="radio"]')].find(
      (input) => normalizeLabel(input.value) === normalizedValue,
    );
    if (radio) {
      radio.checked = true;
      return radio;
    }

    const option = [...group.querySelectorAll("option")].find(
      (input) => normalizeLabel(input.value) === normalizedValue,
    );
    const select = option?.closest("select");
    if (!select) return null;

    select.value = option.value;
    return select;
  };

  /*
   * The shopper's most recent choice is authoritative. If that choice makes
   * the current combination unavailable, retain it and move the other option
   * to the first sellable value in the merchant-defined variant order.
   */
  const resolveUnavailableSpreadconnectSelection = (variantOptions) => {
    const variants = getSpreadconnectVariantData(variantOptions);
    const optionGroups = [
      ...variantOptions.querySelectorAll(".product-form__input"),
    ];
    const selectedValues = optionGroups.map((group) =>
      normalizeLabel(group.querySelector(":checked")?.value),
    );
    const lastChangedIndex = Number(variantOptions.dataset.jpLastChangedOption);

    if (
      !variants ||
      !Number.isInteger(lastChangedIndex) ||
      lastChangedIndex < 0 ||
      lastChangedIndex >= optionGroups.length
    ) {
      return false;
    }

    const currentSelectionIsAvailable = variants.some(
      (variant) =>
        variant.available &&
        selectedValues.every(
          (selectedValue, index) =>
            normalizeLabel(variant.options?.[index]) === selectedValue,
        ),
    );
    if (currentSelectionIsAvailable) return false;

    const preferredVariant = variants.find(
      (variant) =>
        variant.available &&
        normalizeLabel(variant.options?.[lastChangedIndex]) ===
          selectedValues[lastChangedIndex],
    );
    if (!preferredVariant) return false;

    let changeTarget = null;
    optionGroups.forEach((group, optionIndex) => {
      if (
        optionIndex === lastChangedIndex ||
        normalizeLabel(preferredVariant.options?.[optionIndex]) ===
          selectedValues[optionIndex]
      ) {
        return;
      }

      changeTarget =
        selectOptionValue(group, preferredVariant.options?.[optionIndex]) || changeTarget;
    });
    if (!changeTarget) return false;

    variantOptions.dataset.jpAutoResolving = "true";
    try {
      changeTarget.dispatchEvent(new Event("change", { bubbles: true }));
    } finally {
      delete variantOptions.dataset.jpAutoResolving;
    }
    return true;
  };

  const syncSpreadconnectAvailability = (root) => {
    const pickers = root.matches?.("variant-options[data-jp-spreadconnect]")
      ? [root]
      : root.querySelectorAll?.("variant-options[data-jp-spreadconnect]") || [];
    [...pickers].forEach(syncSpreadconnectOptionAvailability);
  };

  const syncAvailabilityAfterWonder = (root) => {
    Promise.all([
      customElements.whenDefined("variant-options"),
      customElements.whenDefined("gallery-section"),
    ]).then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => syncSpreadconnectAvailability(root));
      });
    });
  };

  const hoverImageObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ target }) => {
      const card = target.closest("color-swatch");
      if (card) syncCardHoverInset(card);
    });
  });

  hoverImageObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["src"],
    subtree: true,
  });

  document.addEventListener(
    "change",
    (event) => {
      const control = event.target.closest?.('input[type="radio"], select');
      const variantOptions = control?.closest(
        "variant-options[data-jp-spreadconnect]:not([data-high-variants-product])",
      );
      if (!variantOptions || variantOptions.dataset.jpAutoResolving) return;

      const optionGroups = [
        ...variantOptions.querySelectorAll(".product-form__input"),
      ];
      const changedGroup = control.closest(".product-form__input");
      const changedIndex = optionGroups.indexOf(changedGroup);
      if (changedIndex >= 0) {
        variantOptions.dataset.jpLastChangedOption = String(changedIndex);
      }
    },
    true,
  );

  /*
   * Spreadconnect labels product media as "Product - color". When a shopper
   * chooses one of those thumbnails, keep the selected color variant aligned
   * while leaving unlabelled lifestyle/detail media independent.
   */
  /*
   * Wonder's thumbnail carousel may stop a bubbled click after it has
   * changed the visible media. Listen during capture so the matching color
   * selection is never skipped.
   */
  document.addEventListener("click", (event) => {
    const thumbnail = event.target.closest(
      "gallery-section [data-thumbs] [data-slide-media-id]",
    );
    if (!thumbnail) return;

    const gallery = thumbnail.closest("gallery-section");
    const productSection = gallery?.closest("section[data-product-handle]");
    const handle = productSection?.dataset.productHandle;
    if (!productSection || !handle) return;

    const variantOptions = productSection.querySelector(
      `variant-options[data-product-handle="${CSS.escape(handle)}"]`,
    );
    const imageAlt = normalizeLabel(thumbnail.querySelector("img")?.alt);
    if (!variantOptions?.hasAttribute("data-jp-spreadconnect") || !imageAlt) return;

    const colorInput = [...variantOptions.querySelectorAll('input[name="Color"]')].find(
      (input) => imageAlt.endsWith(` - ${normalizeLabel(input.value)}`),
    );
    if (!colorInput || colorInput.checked || colorInput.disabled) return;

    /*
     * Let Wonder finish this thumbnail click with its normal Swiper behavior.
     * The deferred color change must not rebuild or reposition the gallery,
     * because that would reset both Swipers before the clicked media is shown.
     */
    const mediaId = thumbnail.dataset.slideMediaId;
    const originalFilterSlides = gallery.filterSlides;
    gallery.filterSlides = () => {};
    variantOptions.dataset.jpThumbnailSelection = mediaId;

    const restoreGalleryFiltering = () => {
      gallery.filterSlides = originalFilterSlides;
    };

    const finishThumbnailSelection = (variantEvent) => {
      if (variantEvent.target !== variantOptions) return;

      try {
        restoreGalleryFiltering();
      } finally {
        delete variantOptions.dataset.jpThumbnailSelection;
        document.removeEventListener(
          "variantChangeEnd",
          finishThumbnailSelection,
        );
      }
    };
    document.addEventListener(
      "variantChangeEnd",
      finishThumbnailSelection,
    );

    Promise.resolve().then(() => {
      try {
        colorInput.click();
      } catch (error) {
        restoreGalleryFiltering();
        delete variantOptions.dataset.jpThumbnailSelection;
        document.removeEventListener(
          "variantChangeEnd",
          finishThumbnailSelection,
        );
        throw error;
      }
    });
  }, true);

  /*
   * Direct swatch selections should jump to that color's first source-media
   * thumbnail, not promote it to the beginning of Wonder's gallery. A
   * thumbnail-originated selection is handled above so it can retain the
   * exact image the shopper clicked.
   */
  document.addEventListener("variantChangeEnd", (event) => {
    const variantOptions = event.target;
    if (!variantOptions?.matches?.("variant-options[data-jp-spreadconnect]")) return;

    if (resolveUnavailableSpreadconnectSelection(variantOptions)) return;

    syncSpreadconnectOptionAvailability(variantOptions);
    if (variantOptions.dataset.jpThumbnailSelection) return;

    const productSection = variantOptions.closest("section[data-product-handle]");
    const gallery = productSection?.querySelector("gallery-section");
    const selectedColor = normalizeLabel(
      variantOptions.querySelector('input[name="Color"]:checked')?.value,
    );
    if (!gallery || !selectedColor) return;

    const mediaId = [...gallery.querySelectorAll("[data-thumbs] [data-slide-media-id]")].find(
      (thumbnail) =>
        normalizeLabel(thumbnail.querySelector("img")?.alt).endsWith(` - ${selectedColor}`),
    )?.dataset.slideMediaId;
    if (!mediaId) return;

    requestAnimationFrame(() => {
      gallery.filterSlides?.(variantOptions.options || [], false, true);
      requestAnimationFrame(() => gallery.setActiveMedia?.(mediaId));
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        syncAfterWonder(document);
        syncAvailabilityAfterWonder(document);
      },
      { once: true },
    );
  } else {
    syncAfterWonder(document);
    syncAvailabilityAfterWonder(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    syncAfterWonder(event.target);
    syncAvailabilityAfterWonder(event.target);
  });
})();
